import { API, BACKEND_URL } from '@/config/api';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { APIProvider, Map as GoogleMap, Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import AdminNavigation from '@/components/AdminNavigation';
import PlaceAutocomplete from './SchemaBuilder/PlaceAutocomplete';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  MapPin, Plus, Trash2, Save, Loader2, Search, ArrowLeft, Navigation, Building2, 
  CheckCircle, AlertTriangle, X, Edit2, Eye, Layers, Microscope, Copy, Filter, Bug, Anchor
} from 'lucide-react';

export default function SchemaBuilder() {
  const navigate = useNavigate();
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [customOrganisms, setCustomOrganisms] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showAddLocationDialog, setShowAddLocationDialog] = useState(false);
  const [showAddGroupDialog, setShowAddGroupDialog] = useState(false);
  const [showAddSpeciesDialog, setShowAddSpeciesDialog] = useState(false);
  
  // Area filter for locations
  const [areas, setAreas] = useState([]);
  const [selectedAreaFilter, setSelectedAreaFilter] = useState('all');
  
  // Import organisms from existing template
  const [existingTemplates, setExistingTemplates] = useState([]);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [selectedTemplateForImport, setSelectedTemplateForImport] = useState('');
  
  // Duplicate entire template
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [selectedTemplateForDuplicate, setSelectedTemplateForDuplicate] = useState('');
  
  // Organism type tabs (algae vs zooplankton)
  const [activeOrganismTab, setActiveOrganismTab] = useState('algae');
  
  // Zooplankton organisms
  const [zooplanktonOrganisms, setZooplanktonOrganisms] = useState([]);
  const [newZooplankton, setNewZooplankton] = useState({
    sampleType: 'Håvtrekk',
    organism: ''
  });
  const [showAddZooplanktonDialog, setShowAddZooplanktonDialog] = useState(false);
  const [newZooplanktonName, setNewZooplanktonName] = useState('');
  
  // Zooplankton sample types and organisms
  const [zooplanktonSampleTypes] = useState(['Håvtrekk', 'Visuelt']);
  const [zooplanktonByType, setZooplanktonByType] = useState({
    'Håvtrekk': ['Mikromaneter', 'Kolonimaneter', 'Små maneter', 'Radiolaria', 'Pigghudlarver', 'Foraminifera', 'Hydroider', 'Larver skjell', 'Ribbemanet'],
    'Visuelt': ['Manet', 'Ribbemanet']
  });
  
  // Form for adding organism
  const [newOrganism, setNewOrganism] = useState({
    group: '',
    species: '',
    depth: '3'
  });
  
  // New location form
  const [newLocation, setNewLocation] = useState({
    letter: '',
    name: '',
    address: '',
    latitude: '',
    longitude: ''
  });
  const [mapCenter, setMapCenter] = useState({ lat: 59.25, lng: 5.8 });
  const [markerPosition, setMarkerPosition] = useState(null);
  const [addressValue, setAddressValue] = useState('');
  
  // New group/species
  const [newGroupName, setNewGroupName] = useState('');
  const [newSpeciesName, setNewSpeciesName] = useState('');
  const [speciesSearchTerm, setSpeciesSearchTerm] = useState('');

  // Standard groups and species from BulkSampleEntry
  const [organismGroups, setOrganismGroups] = useState([
    'Kiselalger',
    'Svepeflagellater', 
    'Nåleflagellater',
    'Kiselflagellater',
    'Fureflagellater'
  ]);
  
  const [speciesByGroup, setSpeciesByGroup] = useState({
    'Kiselalger': ['Skeletonema', 'Chaetoceros', 'Pseudo-Nitzschia', 'Div'],
    'Svepeflagellater': ['Chryso/Prymn', 'Phaeocystis'],
    'Nåleflagellater': ['Pseudochatonella'],
    'Kiselflagellater': ['Dictyocha/Vicicitus'],
    'Fureflagellater': ['Alexandrium']
  });

  useEffect(() => {
    fetchLocations();
    fetchAreas();
    fetchTemplates();
    fetchOrganismGroups();
  }, []);

  // Fetch organism groups from database
  const fetchOrganismGroups = async () => {
    try {
      const response = await axios.get(`${API}/organisms`, { withCredentials: true });
      const allOrganisms = response.data.filter(org => org.type === 'algae');
      
      // Build groups and species map from database
      const groupsFromDB = new Set();
      const speciesMap = {};
      
      allOrganisms.forEach(org => {
        const group = org.group || 'Ukjent';
        groupsFromDB.add(group);
        
        if (!speciesMap[group]) {
          speciesMap[group] = [];
        }
        if (org.species && !speciesMap[group].includes(org.species)) {
          speciesMap[group].push(org.species);
        }
      });
      
      // Update state with data from database
      const newGroups = Array.from(groupsFromDB);
      if (newGroups.length > 0) {
        setOrganismGroups(prev => {
          // Merge with existing groups (keep order of existing, add new at end)
          const merged = [...prev];
          newGroups.forEach(g => {
            if (!merged.includes(g)) {
              merged.push(g);
            }
          });
          return merged;
        });
        
        setSpeciesByGroup(prev => ({
          ...prev,
          ...speciesMap
        }));
      }
    } catch (error) {
      console.error('Error fetching organism groups:', error);
    }
  };

  const fetchLocations = async () => {
    const demoUser = localStorage.getItem('demo_user');
    if (demoUser) {
      // Demo locations for demonstration purposes
      setAvailableLocations([
        { id: 'loc-a', letter: 'A', name: 'Toftøy', full_name: 'Lokalitet A - Toftøy', area_id: 'p02-rogaland' },
        { id: 'loc-b', letter: 'B', name: 'Føresvik', full_name: 'Lokalitet B - Føresvik', area_id: 'p02-rogaland' },
        { id: 'loc-c', letter: 'C', name: 'Rosshl.', full_name: 'Lokalitet C - Rosshl.', area_id: 'p02-rogaland' },
        { id: 'loc-d', letter: 'D', name: 'Høgsfj.', full_name: 'Lokalitet D - Høgsfj.', area_id: 'p02-rogaland' },
        { id: 'loc-e', letter: 'E', name: 'Finnøyfj.', full_name: 'Lokalitet E - Finnøyfj.', area_id: 'p02-rogaland' },
        { id: 'loc-f', letter: 'F', name: 'Årdalsfj.', full_name: 'Lokalitet F - Årdalsfj.', area_id: 'p02-rogaland' },
        { id: 'loc-g', letter: 'G', name: 'Jøsenfj.', full_name: 'Lokalitet G - Jøsenfj.', area_id: 'p02-rogaland' },
        { id: 'loc-h', letter: 'H', name: 'Erfj.', full_name: 'Lokalitet H - Erfj.', area_id: 'p02-rogaland' },
        { id: 'loc-i', letter: 'I', name: 'Jelsa', full_name: 'Lokalitet I - Jelsa', area_id: 'p02-rogaland' },
        { id: 'loc-j', letter: 'J', name: 'Vindafj.', full_name: 'Lokalitet J - Vindafj.', area_id: 'p02-rogaland' },
        { id: 'loc-k', letter: 'K', name: 'Rott', full_name: 'Lokalitet K - Rott', area_id: 'p02-rogaland' },
        { id: 'loc-l', letter: 'L', name: 'Boknafj.', full_name: 'Lokalitet L - Boknafj.', area_id: 'p02-rogaland' }
      ]);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/locations`, { withCredentials: true });
      const locs = response.data.map(loc => ({
        ...loc,
        full_name: `Lokalitet ${loc.letter} - ${loc.name}`
      }));
      setAvailableLocations(locs);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchAreas = async () => {
    try {
      const response = await axios.get(`${API}/areas`, { withCredentials: true });
      setAreas(response.data);
    } catch (error) {
      console.error('Error fetching areas:', error);
      // Fallback areas
      setAreas([
        { id: 'p02-rogaland', name: 'P02 Rogaland' },
        { id: 'p03-rogaland', name: 'P03 Rogaland' },
        { id: 'p04-hordaland', name: 'P04 Hordaland' }
      ]);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/bulk-templates`, { withCredentials: true });
      setExistingTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  // Filter locations by selected area
  const filteredLocations = selectedAreaFilter === 'all' 
    ? availableLocations 
    : availableLocations.filter(loc => loc.area_id === selectedAreaFilter);

  // Import organisms from another template
  const importOrganismsFromTemplate = () => {
    if (!selectedTemplateForImport) {
      toast.error('Velg et skjema å importere fra');
      return;
    }
    
    const template = existingTemplates.find(t => t.id === selectedTemplateForImport);
    if (!template || !template.config?.organisms) {
      toast.error('Ingen organismer funnet i valgt skjema');
      return;
    }
    
    const newOrganisms = template.config.organisms.map(org => ({
      id: `${org.group}_${org.species}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      group: org.group,
      species: org.species,
      depth: org.depth || '3'
    }));
    
    // Add without duplicating existing ones
    const existingKeys = customOrganisms.map(o => `${o.group}_${o.species}`);
    const uniqueNewOrganisms = newOrganisms.filter(o => !existingKeys.includes(`${o.group}_${o.species}`));
    
    if (uniqueNewOrganisms.length === 0) {
      toast.info('Alle organismer fra dette skjemaet er allerede lagt til');
    } else {
      setCustomOrganisms(prev => [...prev, ...uniqueNewOrganisms]);
      toast.success(`${uniqueNewOrganisms.length} alge-organismer importert fra "${template.name}"`);
    }
    
    // Also import zooplankton if available
    if (template.config?.zooplankton) {
      const newZooplankton = template.config.zooplankton.map(z => ({
        id: `${z.sampleType}_${z.organism}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        sampleType: z.sampleType,
        organism: z.organism
      }));
      
      const existingZooKeys = zooplanktonOrganisms.map(z => `${z.sampleType}_${z.organism}`);
      const uniqueNewZoo = newZooplankton.filter(z => !existingZooKeys.includes(`${z.sampleType}_${z.organism}`));
      
      if (uniqueNewZoo.length > 0) {
        setZooplanktonOrganisms(prev => [...prev, ...uniqueNewZoo]);
        toast.success(`${uniqueNewZoo.length} zooplankton importert`);
      }
    }
    
    setShowImportDialog(false);
    setSelectedTemplateForImport('');
  };

  // Duplicate entire template - copies organisms, zooplankton, and suggests new name
  const duplicateTemplate = () => {
    if (!selectedTemplateForDuplicate) {
      toast.error('Velg et skjema å duplisere');
      return;
    }
    
    const template = existingTemplates.find(t => t.id === selectedTemplateForDuplicate);
    if (!template) {
      toast.error('Skjema ikke funnet');
      return;
    }
    
    // Clear current data first
    setCustomOrganisms([]);
    setZooplanktonOrganisms([]);
    setSelectedLocations([]);
    
    // Copy organisms
    if (template.config?.organisms?.length > 0) {
      const newOrganisms = template.config.organisms.map(org => ({
        ...org,
        id: `${org.group}_${org.species}_${Date.now()}_${Math.random()}`
      }));
      setCustomOrganisms(newOrganisms);
    }
    
    // Copy zooplankton
    if (template.config?.zooplankton?.length > 0) {
      const newZoo = template.config.zooplankton.map(z => ({
        ...z,
        id: `${z.sampleType}_${z.organism}_${Date.now()}_${Math.random()}`
      }));
      setZooplanktonOrganisms(newZoo);
    }
    
    // Suggest new name
    const baseName = template.name.replace(/\s*\(kopi\d*\)\s*$/, '');
    setTemplateName(`${baseName} (kopi)`);
    setTemplateDescription(template.description || '');
    
    toast.success(
      `Mal duplisert! ${template.config?.organisms?.length || 0} alger og ${template.config?.zooplankton?.length || 0} zooplankton kopiert. Velg område og lokalitetar, så lagre.`,
      { duration: 5000 }
    );
    
    setShowDuplicateDialog(false);
    setSelectedTemplateForDuplicate('');
  };

  // Add zooplankton organism
  const addZooplankton = () => {
    if (!newZooplankton.sampleType || !newZooplankton.organism) {
      toast.error('Velg prøvetype og organisme');
      return;
    }
    
    const exists = zooplanktonOrganisms.some(
      z => z.sampleType === newZooplankton.sampleType && z.organism === newZooplankton.organism
    );
    
    if (exists) {
      toast.error('Denne zooplankton er allerede lagt til');
      return;
    }
    
    const newZoo = {
      id: `${newZooplankton.sampleType}_${newZooplankton.organism}_${Date.now()}`,
      sampleType: newZooplankton.sampleType,
      organism: newZooplankton.organism
    };
    
    setZooplanktonOrganisms(prev => [...prev, newZoo]);
    setNewZooplankton({ sampleType: 'Håvtrekk', organism: '' });
    toast.success('Zooplankton lagt til!');
  };

  // Remove zooplankton
  const removeZooplankton = (id) => {
    setZooplanktonOrganisms(prev => prev.filter(z => z.id !== id));
  };

  // Add new zooplankton type
  const addNewZooplanktonType = () => {
    if (!newZooplanktonName.trim()) {
      toast.error('Skriv inn navn på organismen');
      return;
    }
    
    const currentType = newZooplankton.sampleType;
    if (zooplanktonByType[currentType]?.includes(newZooplanktonName)) {
      toast.error('Denne organismen finnes allerede');
      return;
    }
    
    setZooplanktonByType(prev => ({
      ...prev,
      [currentType]: [...(prev[currentType] || []), newZooplanktonName]
    }));
    setNewZooplankton(prev => ({ ...prev, organism: newZooplanktonName }));
    setNewZooplanktonName('');
    setShowAddZooplanktonDialog(false);
    toast.success('Ny zooplankton-type lagt til!');
  };

  const toggleLocation = (locationId) => {
    setSelectedLocations(prev =>
      prev.includes(locationId)
        ? prev.filter(id => id !== locationId)
        : [...prev, locationId]
    );
  };

  // Select/deselect all filtered locations
  const toggleAllFilteredLocations = () => {
    const filteredIds = filteredLocations.map(loc => loc.id);
    const allSelected = filteredIds.every(id => selectedLocations.includes(id));
    
    if (allSelected) {
      // Deselect all filtered
      setSelectedLocations(prev => prev.filter(id => !filteredIds.includes(id)));
    } else {
      // Select all filtered
      setSelectedLocations(prev => [...new Set([...prev, ...filteredIds])]);
    }
  };

  const addNewLocation = () => {
    if (!newLocation.letter || !newLocation.name) {
      toast.error('Fyll ut bokstav og navn');
      return;
    }
    
    if (!newLocation.latitude || !newLocation.longitude) {
      toast.error('Velg posisjon på kartet');
      return;
    }

    const newLoc = {
      id: `loc-custom-${Date.now()}`,
      letter: newLocation.letter.toUpperCase(),
      name: newLocation.name,
      address: newLocation.address || '',
      latitude: parseFloat(newLocation.latitude),
      longitude: parseFloat(newLocation.longitude),
      full_name: `Lokalitet ${newLocation.letter.toUpperCase()} - ${newLocation.name}`,
      area_id: selectedAreaFilter !== 'all' ? selectedAreaFilter : null
    };

    setAvailableLocations(prev => [...prev, newLoc]);
    setSelectedLocations(prev => [...prev, newLoc.id]);
    setShowAddLocationDialog(false);
    setNewLocation({ letter: '', name: '', address: '', latitude: '', longitude: '' });
    setMarkerPosition(null);
    setAddressValue('');
    setMapCenter({ lat: 59.25, lng: 5.8 });
    toast.success('Lokalitet lagt til!');
  };

  const handlePlaceSelect = (place) => {
    setMapCenter({ lat: place.lat, lng: place.lng });
    setMarkerPosition({ lat: place.lat, lng: place.lng });
    setNewLocation({
      ...newLocation,
      address: place.address,
      latitude: place.lat.toFixed(6),
      longitude: place.lng.toFixed(6)
    });
  };

  const handleMapClick = (e) => {
    if (!e.detail || !e.detail.latLng) return;
    
    const lat = e.detail.latLng.lat;
    const lng = e.detail.latLng.lng;
    
    setMarkerPosition({ lat, lng });
    setNewLocation({
      ...newLocation,
      latitude: lat.toFixed(6),
      longitude: lng.toFixed(6)
    });
  };

  const handleMarkerDrag = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    
    setMarkerPosition({ lat, lng });
    setNewLocation({
      ...newLocation,
      latitude: lat.toFixed(6),
      longitude: lng.toFixed(6)
    });
  };

  const addNewGroup = () => {
    if (!newGroupName.trim()) {
      toast.error('Gruppenavn er påkrevd');
      return;
    }

    if (organismGroups.includes(newGroupName)) {
      toast.error('Denne gruppen finnes allerede');
      return;
    }

    setOrganismGroups(prev => [...prev, newGroupName]);
    setSpeciesByGroup(prev => ({ ...prev, [newGroupName]: [] }));
    setNewGroupName('');
    setShowAddGroupDialog(false);
    toast.success('Ny gruppe lagt til!');
  };

  const addNewSpecies = () => {
    if (!newOrganism.group) {
      toast.error('Velg gruppe først');
      return;
    }

    if (!newSpeciesName.trim()) {
      toast.error('Artsnavn er påkrevd');
      return;
    }

    setSpeciesByGroup(prev => ({
      ...prev,
      [newOrganism.group]: [...(prev[newOrganism.group] || []), newSpeciesName]
    }));
    
    setNewOrganism({ ...newOrganism, species: newSpeciesName });
    setNewSpeciesName('');
    setShowAddSpeciesDialog(false);
    toast.success('Ny art lagt til!');
  };

  const addOrganism = () => {
    if (!newOrganism.group || !newOrganism.species) {
      toast.error('Velg gruppe og art');
      return;
    }

    const organism = {
      id: `${newOrganism.group}_${newOrganism.species}_${Date.now()}`,
      group: newOrganism.group,
      species: newOrganism.species,
      depth: newOrganism.depth,
      isSpecies: true
    };

    setCustomOrganisms(prev => [...prev, organism]);
    setNewOrganism({ group: '', species: '', depth: '3' });
    setSpeciesSearchTerm('');
    toast.success('Organisme lagt til');
  };

  const removeOrganism = (id) => {
    setCustomOrganisms(prev => prev.filter(org => org.id !== id));
  };

  const saveTemplate = async () => {
    if (!templateName) {
      toast.error('Template-navn er påkrevd');
      return;
    }

    if (selectedLocations.length === 0) {
      toast.error('Velg minst én lokalitet');
      return;
    }

    if (customOrganisms.length === 0 && zooplanktonOrganisms.length === 0) {
      toast.error('Legg til minst én organisme (alge eller zooplankton)');
      return;
    }

    const template = {
      name: templateName,
      description: templateDescription,
      is_default: isDefault,
      config: {
        locations: selectedLocations.map(locId => 
          availableLocations.find(l => l.id === locId)
        ),
        organisms: customOrganisms,
        zooplankton: zooplanktonOrganisms
      }
    };

    const demoUser = localStorage.getItem('demo_user');
    if (demoUser) {
      // Save to localStorage for demo
      const saved = JSON.parse(localStorage.getItem('bulk_templates') || '[]');
      const newTemplate = {
        id: `template-${Date.now()}`,
        ...template,
        created_by: 'demo-user',
        created_at: new Date().toISOString()
      };
      saved.push(newTemplate);
      localStorage.setItem('bulk_templates', JSON.stringify(saved));
      toast.success('Template lagret!');
      navigate('/admin/samples/bulk');
      return;
    }

    try {
      await axios.post(`${API}/bulk-templates`, template, { withCredentials: true });
      toast.success('Template lagret!');
      navigate('/admin/samples/bulk');
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Kunne ikke lagre template');
    }
  };

  const selectedLocationsList = selectedLocations.map(id => 
    availableLocations.find(l => l.id === id)
  ).filter(Boolean);

  // Filter species based on search
  const getFilteredSpecies = () => {
    if (!newOrganism.group || !speciesByGroup[newOrganism.group]) return [];
    
    const species = speciesByGroup[newOrganism.group];
    if (!speciesSearchTerm) return species;
    
    return species.filter(s => 
      s.toLowerCase().includes(speciesSearchTerm.toLowerCase())
    );
  };

  return (
    <>
      <AdminNavigation />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/20 to-teal-50/20">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="mb-8">
            <Button
              onClick={() => navigate('/admin/samples/bulk')}
              variant="ghost"
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Tilbake til registrering
            </Button>
            
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Layers className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                  Bygg custom skjema
                </h1>
                <p className="text-slate-600">Velg lokaliteter og organismer for din prøvetaking</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-5 gap-8">
            {/* Left: Configuration */}
            <div className="xl:col-span-3 space-y-6">
              {/* Template info */}
              <Card className="shadow-xl border-0">
                <div className="h-1.5 bg-gradient-to-r from-purple-500 to-pink-500"></div>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <span className="font-bold text-purple-600">1</span>
                      </div>
                      <span>Template-informasjon</span>
                    </div>
                    
                    {/* Duplicate template button */}
                    <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="outline" className="border-purple-300 text-purple-600 hover:bg-purple-50">
                          <Copy className="w-4 h-4 mr-1" />
                          Dupliser mal
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg">
                        <DialogHeader>
                          <DialogTitle className="text-xl">Dupliser eksisterende mal</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                            <p className="text-sm text-purple-800">
                              <strong>💡 Tips:</strong> Duplisering kopierer alle organismer og zooplankton fra ein eksisterande mal. 
                              Du vel så nytt område og lokalitetar før du lagrar.
                            </p>
                          </div>
                          <div>
                            <Label className="font-semibold mb-2 block">Velg mal å duplisere</Label>
                            <Select
                              value={selectedTemplateForDuplicate}
                              onValueChange={setSelectedTemplateForDuplicate}
                            >
                              <SelectTrigger className="h-14 border-2">
                                <SelectValue placeholder="Velg ein mal..." />
                              </SelectTrigger>
                              <SelectContent>
                                {existingTemplates.map(template => (
                                  <SelectItem key={template.id} value={template.id}>
                                    <div className="flex flex-col py-1">
                                      <span className="font-semibold">{template.name}</span>
                                      <span className="text-xs text-slate-500">
                                        {template.config?.organisms?.length || 0} alger • {template.config?.zooplankton?.length || 0} zooplankton
                                      </span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {selectedTemplateForDuplicate && (
                            <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                              <p className="text-sm text-green-800">
                                ✅ Klar til å duplisere! Etter duplisering kan du:
                              </p>
                              <ul className="text-sm text-green-700 mt-2 space-y-1 ml-4">
                                <li>• Endre mal-namn</li>
                                <li>• Velge nytt område (f.eks. P03)</li>
                                <li>• Velge lokalitetar for det nye området</li>
                                <li>• Lagre som ny mal</li>
                              </ul>
                            </div>
                          )}
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setShowDuplicateDialog(false)}>
                            Avbryt
                          </Button>
                          <Button 
                            onClick={duplicateTemplate}
                            disabled={!selectedTemplateForDuplicate}
                            className="bg-purple-600 hover:bg-purple-700"
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            Dupliser mal
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-sm font-semibold">Template-navn *</Label>
                    <Input
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      placeholder="f.eks. 'Hurtigtest 4 stasjonar'"
                      className="h-12 text-base border-2 border-slate-200 focus:border-purple-500 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold">Beskrivelse (valgfritt)</Label>
                    <Input
                      value={templateDescription}
                      onChange={(e) => setTemplateDescription(e.target.value)}
                      placeholder="Kort beskrivelse av når denne brukes"
                      className="h-12 border-2 border-slate-200 focus:border-purple-500 rounded-xl"
                    />
                  </div>
                  <div className="flex items-center space-x-2 pt-2">
                    <Checkbox
                      id="is-default"
                      checked={isDefault}
                      onCheckedChange={setIsDefault}
                    />
                    <label
                      htmlFor="is-default"
                      className="text-sm font-medium text-slate-700 cursor-pointer"
                    >
                      Bruk som standard template
                    </label>
                  </div>
                </CardContent>
              </Card>

              {/* Locations selector */}
              <Card className="shadow-xl border-0">
                <div className="h-1.5 bg-gradient-to-r from-teal-500 to-blue-500"></div>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
                        <span className="font-bold text-teal-600">2</span>
                      </div>
                      <span>Velg lokaliteter</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-normal text-slate-600">
                        {selectedLocations.length} valgt
                      </span>
                      <Dialog open={showAddLocationDialog} onOpenChange={setShowAddLocationDialog}>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="outline" className="border-teal-300 text-teal-600 hover:bg-teal-50">
                            <Plus className="w-4 h-4 mr-1" />
                            Ny lokalitet
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle className="text-2xl">Legg til ny lokalitet</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-6">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label className="font-semibold mb-2 block">Bokstav *</Label>
                                <Input
                                  value={newLocation.letter}
                                  onChange={(e) => setNewLocation({ ...newLocation, letter: e.target.value.toUpperCase() })}
                                  placeholder="M"
                                  maxLength={1}
                                  className="h-14 text-3xl font-bold text-center border-2 border-slate-200 focus:border-teal-500 rounded-xl"
                                />
                              </div>
                              <div>
                                <Label className="font-semibold mb-2 block">Navn *</Label>
                                <Input
                                  value={newLocation.name}
                                  onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
                                  placeholder="Stedsnavn"
                                  className="h-14 text-base border-2 border-slate-200 focus:border-teal-500 rounded-xl"
                                />
                              </div>
                            </div>
                            
                            <div>
                              <Label className="font-semibold mb-2 block">Søk adresse (valgfritt)</Label>
                              <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
                                <PlaceAutocomplete
                                  onPlaceSelect={handlePlaceSelect}
                                  value={addressValue}
                                  onChange={setAddressValue}
                                />
                              </APIProvider>
                            </div>
                            
                            <div>
                              <Label className="font-semibold mb-2 block">Velg posisjon på kart</Label>
                              <div className="h-96 rounded-xl overflow-hidden border-2 border-slate-200">
                                <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
                                  <Map
                                    center={mapCenter}
                                    zoom={markerPosition ? 15 : 10}
                                    mapId="schema-location-map"
                                    onClick={handleMapClick}
                                    gestureHandling="greedy"
                                    zoomControl={true}
                                    mapTypeControl={true}
                                    scrollwheel={true}
                                  >
                                    {markerPosition && (
                                      <AdvancedMarker
                                        position={markerPosition}
                                        draggable={true}
                                        onDragEnd={handleMarkerDrag}
                                      >
                                        <div className="relative">
                                          <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-blue-600 rounded-full border-4 border-white shadow-2xl flex items-center justify-center cursor-move">
                                            <MapPin className="w-7 h-7 text-white" />
                                          </div>
                                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-2 h-8 bg-gradient-to-b from-teal-600/60 to-transparent"></div>
                                        </div>
                                      </AdvancedMarker>
                                    )}
                                  </Map>
                                </APIProvider>
                              </div>
                              <p className="text-xs text-slate-500 mt-2">
                                💡 Søk adresse eller klikk på kartet. Pin kan dras.
                              </p>
                            </div>
                            
                            {markerPosition && (
                              <div className="p-4 bg-teal-50 border-2 border-teal-200 rounded-xl">
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                  <div>
                                    <span className="text-slate-600">Latitude:</span>
                                    <span className="font-mono font-bold text-slate-900 ml-2">{newLocation.latitude}°</span>
                                  </div>
                                  <div>
                                    <span className="text-slate-600">Longitude:</span>
                                    <span className="font-mono font-bold text-slate-900 ml-2">{newLocation.longitude}°</span>
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            <div className="flex space-x-2 pt-4 border-t border-slate-200">
                              <Button 
                                onClick={addNewLocation} 
                                className="flex-1 h-12 bg-teal-600 hover:bg-teal-700"
                                disabled={!newLocation.latitude || !newLocation.longitude}
                              >
                                {markerPosition ? 'Legg til lokalitet' : 'Velg posisjon på kart'}
                              </Button>
                              <Button 
                                variant="outline" 
                                onClick={() => {
                                  setShowAddLocationDialog(false);
                                  setMarkerPosition(null);
                                  setAddressValue('');
                                  setNewLocation({ letter: '', name: '', address: '', latitude: '', longitude: '' });
                                }}
                                className="h-12 px-8"
                              >
                                Avbryt
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Area filter */}
                  <div className="mb-4 flex flex-wrap items-center gap-3">
                    <div className="flex items-center space-x-2 text-sm text-slate-600">
                      <Filter className="w-4 h-4" />
                      <span>Filtrer på område:</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant={selectedAreaFilter === 'all' ? 'default' : 'outline'}
                        onClick={() => setSelectedAreaFilter('all')}
                        className={selectedAreaFilter === 'all' ? 'bg-teal-600 hover:bg-teal-700' : ''}
                      >
                        Alle ({availableLocations.length})
                      </Button>
                      {areas.map(area => {
                        const count = availableLocations.filter(loc => loc.area_id === area.id).length;
                        return (
                          <Button
                            key={area.id}
                            size="sm"
                            variant={selectedAreaFilter === area.id ? 'default' : 'outline'}
                            onClick={() => setSelectedAreaFilter(area.id)}
                            className={selectedAreaFilter === area.id ? 'bg-teal-600 hover:bg-teal-700' : ''}
                          >
                            {area.name} ({count})
                          </Button>
                        );
                      })}
                    </div>
                    {filteredLocations.length > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={toggleAllFilteredLocations}
                        className="text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                      >
                        {filteredLocations.every(loc => selectedLocations.includes(loc.id)) 
                          ? 'Fjern alle synlige' 
                          : 'Velg alle synlige'}
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {filteredLocations.map((location) => (
                      <div
                        key={location.id}
                        className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${
                          selectedLocations.includes(location.id)
                            ? 'border-teal-500 bg-teal-50'
                            : 'border-slate-200 hover:border-slate-300 bg-white'
                        }`}
                        onClick={() => toggleLocation(location.id)}
                      >
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            checked={selectedLocations.includes(location.id)}
                            onCheckedChange={() => toggleLocation(location.id)}
                          />
                          <div className="flex items-center space-x-2">
                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                              selectedLocations.includes(location.id)
                                ? 'bg-teal-500 text-white'
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              <span className="text-xs font-bold">{location.letter}</span>
                            </div>
                            <span className="text-sm font-medium text-slate-700">{location.name}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {filteredLocations.length === 0 && (
                    <div className="text-center py-8 text-slate-500">
                      Ingen lokaliteter funnet for dette området
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Organisms builder */}
              <Card className="shadow-xl border-0">
                <div className="h-1.5 bg-gradient-to-r from-blue-500 to-indigo-500"></div>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <span className="font-bold text-blue-600">3</span>
                      </div>
                      <span>Legg til organismer</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-normal text-slate-600">
                        {customOrganisms.length} alger, {zooplanktonOrganisms.length} zooplankton
                      </span>
                      {/* Import from existing template button */}
                      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="outline" className="border-blue-300 text-blue-600 hover:bg-blue-50">
                            <Copy className="w-4 h-4 mr-1" />
                            Kopier fra skjema
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Kopier organismer fra eksisterende skjema</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div>
                              <Label className="font-semibold mb-2 block">Velg skjema å kopiere fra</Label>
                              <Select
                                value={selectedTemplateForImport}
                                onValueChange={setSelectedTemplateForImport}
                              >
                                <SelectTrigger className="h-12 border-2">
                                  <SelectValue placeholder="Velg et skjema..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {existingTemplates.map(template => (
                                    <SelectItem key={template.id} value={template.id}>
                                      <div className="flex flex-col">
                                        <span className="font-medium">{template.name}</span>
                                        <span className="text-xs text-slate-500">
                                          {template.config?.organisms?.length || 0} alger, {template.config?.zooplankton?.length || 0} zooplankton
                                        </span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            {selectedTemplateForImport && (
                              <div className="p-3 bg-blue-50 rounded-lg">
                                <p className="text-sm text-blue-700">
                                  <strong>Tips:</strong> Duplikater blir automatisk filtrert bort
                                </p>
                              </div>
                            )}
                          </div>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
                              Avbryt
                            </Button>
                            <Button 
                              onClick={importOrganismsFromTemplate}
                              disabled={!selectedTemplateForImport}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              <Copy className="w-4 h-4 mr-2" />
                              Importer organismer
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Tabs for Algae vs Zooplankton */}
                  <div className="flex space-x-2 mb-6">
                    <Button
                      variant={activeOrganismTab === 'algae' ? 'default' : 'outline'}
                      onClick={() => setActiveOrganismTab('algae')}
                      className={activeOrganismTab === 'algae' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                    >
                      <Microscope className="w-4 h-4 mr-2" />
                      Alger ({customOrganisms.length})
                    </Button>
                    <Button
                      variant={activeOrganismTab === 'zooplankton' ? 'default' : 'outline'}
                      onClick={() => setActiveOrganismTab('zooplankton')}
                      className={activeOrganismTab === 'zooplankton' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                    >
                      <Bug className="w-4 h-4 mr-2" />
                      Zooplankton ({zooplanktonOrganisms.length})
                    </Button>
                  </div>

                  {/* ALGAE TAB */}
                  {activeOrganismTab === 'algae' && (
                    <>
                      {/* Add organism form */}
                  <div className="p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border-2 border-slate-200 mb-6">
                    <div className="space-y-4">
                      {/* Group selector with add new */}
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">1. Velg Orden/gruppe *</Label>
                        <div className="flex space-x-2">
                          <Select 
                            value={newOrganism.group} 
                            onValueChange={(value) => {
                              setNewOrganism({ ...newOrganism, group: value, species: '' });
                              setSpeciesSearchTerm('');
                            }}
                          >
                            <SelectTrigger className="h-12 border-2 border-slate-200 focus:border-blue-500 rounded-xl flex-1">
                              <SelectValue placeholder="Velg orden/gruppe først" />
                            </SelectTrigger>
                            <SelectContent>
                              {organismGroups.map((group) => (
                                <SelectItem key={group} value={group}>
                                  <div className="flex items-center space-x-2">
                                    <Microscope className="w-4 h-4 text-blue-600" />
                                    <span>{group}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Dialog open={showAddGroupDialog} onOpenChange={setShowAddGroupDialog}>
                            <DialogTrigger asChild>
                              <Button variant="outline" className="h-12 px-4 border-2 hover:border-blue-500 hover:bg-blue-50">
                                <Plus className="w-4 h-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Legg til ny orden/gruppe</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div>
                                  <Label>Gruppenavn *</Label>
                                  <Input
                                    value={newGroupName}
                                    onChange={(e) => setNewGroupName(e.target.value)}
                                    placeholder="f.eks. Cryptophyceae"
                                    className="h-12"
                                  />
                                </div>
                                <div className="flex space-x-2">
                                  <Button onClick={addNewGroup} className="flex-1 bg-blue-600 hover:bg-blue-700">
                                    Legg til gruppe
                                  </Button>
                                  <Button variant="outline" onClick={() => setShowAddGroupDialog(false)} className="flex-1">
                                    Avbryt
                                  </Button>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </div>

                      {/* Species selector with search and add new */}
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">2. Velg Art *</Label>
                        <div className="flex space-x-2">
                          <Select 
                            value={newOrganism.species}
                            onValueChange={(value) => setNewOrganism({ ...newOrganism, species: value })}
                            disabled={!newOrganism.group}
                          >
                            <SelectTrigger className="h-12 border-2 border-slate-200 focus:border-blue-500 rounded-xl flex-1">
                              <SelectValue placeholder={newOrganism.group ? "Velg art" : "Velg gruppe først"} />
                            </SelectTrigger>
                            <SelectContent>
                              {/* Search field */}
                              <div className="p-2 border-b border-slate-200">
                                <div className="relative">
                                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                                  <Input
                                    value={speciesSearchTerm}
                                    onChange={(e) => setSpeciesSearchTerm(e.target.value)}
                                    placeholder="Søk arter..."
                                    className="pl-9 h-9 text-sm"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                              </div>
                              {getFilteredSpecies().map((species) => (
                                <SelectItem key={species} value={species}>
                                  <div className="flex items-center space-x-2">
                                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                    <span>{species}</span>
                                  </div>
                                </SelectItem>
                              ))}
                              {getFilteredSpecies().length === 0 && newOrganism.group && speciesSearchTerm && (
                                <button
                                  onClick={() => {
                                    setNewSpeciesName(speciesSearchTerm);
                                    setShowAddSpeciesDialog(true);
                                  }}
                                  className="w-full p-4 text-left hover:bg-blue-50 transition-colors border-t border-slate-200"
                                >
                                  <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
                                      <Plus className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                      <div className="text-sm font-semibold text-slate-900">
                                        Legg til &quot;{speciesSearchTerm}&quot;
                                      </div>
                                      <div className="text-xs text-slate-500">
                                        Ny art under {newOrganism.group}
                                      </div>
                                    </div>
                                  </div>
                                </button>
                              )}
                              {getFilteredSpecies().length === 0 && newOrganism.group && !speciesSearchTerm && (
                                <div className="p-3 text-center text-sm text-slate-500">
                                  Søk eller velg art
                                </div>
                              )}
                            </SelectContent>
                          </Select>
                          <Dialog open={showAddSpeciesDialog} onOpenChange={setShowAddSpeciesDialog}>
                            <DialogTrigger asChild>
                              <Button 
                                variant="outline" 
                                className="h-12 px-4 border-2 hover:border-blue-500 hover:bg-blue-50"
                                disabled={!newOrganism.group}
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Legg til ny art</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div>
                                  <Label>Gruppe</Label>
                                  <Input
                                    value={newOrganism.group}
                                    readOnly
                                    className="h-12 bg-slate-50"
                                  />
                                </div>
                                <div>
                                  <Label>Artsnavn *</Label>
                                  <Input
                                    value={newSpeciesName}
                                    onChange={(e) => setNewSpeciesName(e.target.value)}
                                    placeholder="f.eks. Dinophysis"
                                    className="h-12"
                                  />
                                </div>
                                <div className="flex space-x-2">
                                  <Button onClick={addNewSpecies} className="flex-1 bg-blue-600 hover:bg-blue-700">
                                    Legg til art
                                  </Button>
                                  <Button variant="outline" onClick={() => setShowAddSpeciesDialog(false)} className="flex-1">
                                    Avbryt
                                  </Button>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </div>

                      {/* Depth input */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-semibold text-slate-700 mb-2 block">3. Djup (meter)</Label>
                          <Input
                            type="number"
                            value={newOrganism.depth}
                            onChange={(e) => setNewOrganism({ ...newOrganism, depth: e.target.value })}
                            className="h-12 text-base border-2 border-slate-200 focus:border-blue-500 rounded-xl"
                            placeholder="3"
                          />
                        </div>
                        <div className="flex items-end">
                          <Button
                            onClick={addOrganism}
                            disabled={!newOrganism.group || !newOrganism.species}
                            className="w-full h-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Legg til alge
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Organisms list */}
                  {customOrganisms.length === 0 ? (
                    <div className="text-center py-12 bg-slate-50 rounded-xl border-2 border-dashed border-slate-300">
                      <Microscope className="w-16 h-16 mx-auto mb-3 text-slate-300" />
                      <p className="text-sm font-semibold text-slate-600 mb-1">Ingen alger lagt til ennå</p>
                      <p className="text-xs text-slate-400">Velg gruppe og art over for å legge til</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {customOrganisms.map((organism, index) => (
                        <div
                          key={organism.id}
                          className="flex items-center justify-between p-4 bg-white rounded-xl border-2 border-slate-200 hover:border-blue-300 hover:shadow-md transition-all"
                        >
                          <div className="flex items-center space-x-4">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center">
                              <span className="text-sm font-bold bg-gradient-to-br from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                                {index + 1}
                              </span>
                            </div>
                            <div>
                              <div className="font-bold text-slate-900 text-base">{organism.species}</div>
                              <div className="text-xs text-slate-600 flex items-center space-x-2">
                                <span>{organism.group}</span>
                                <span className="text-slate-400">•</span>
                                <span className="font-semibold text-blue-600">{organism.depth}m djup</span>
                              </div>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeOrganism(organism.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                    </>
                  )}

                  {/* ZOOPLANKTON TAB */}
                  {activeOrganismTab === 'zooplankton' && (
                    <>
                      {/* Add zooplankton form */}
                      <div className="p-6 bg-gradient-to-br from-slate-50 to-purple-50 rounded-2xl border-2 border-slate-200 mb-6">
                        <div className="space-y-4">
                          {/* Sample type selector */}
                          <div>
                            <Label className="text-sm font-semibold text-slate-700 mb-2 block">1. Velg Prøvetype *</Label>
                            <Select 
                              value={newZooplankton.sampleType} 
                              onValueChange={(value) => setNewZooplankton({ ...newZooplankton, sampleType: value, organism: '' })}
                            >
                              <SelectTrigger className="h-12 border-2 border-slate-200 focus:border-purple-500 rounded-xl">
                                <SelectValue placeholder="Velg prøvetype" />
                              </SelectTrigger>
                              <SelectContent>
                                {zooplanktonSampleTypes.map((type) => (
                                  <SelectItem key={type} value={type}>
                                    <div className="flex items-center space-x-2">
                                      <Anchor className="w-4 h-4 text-purple-600" />
                                      <span>{type}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Organism selector */}
                          <div>
                            <Label className="text-sm font-semibold text-slate-700 mb-2 block">2. Velg Organisme *</Label>
                            <div className="flex space-x-2">
                              <Select 
                                value={newZooplankton.organism} 
                                onValueChange={(value) => setNewZooplankton({ ...newZooplankton, organism: value })}
                              >
                                <SelectTrigger className="h-12 border-2 border-slate-200 focus:border-purple-500 rounded-xl flex-1">
                                  <SelectValue placeholder="Velg organisme" />
                                </SelectTrigger>
                                <SelectContent>
                                  {(zooplanktonByType[newZooplankton.sampleType] || []).map((org) => (
                                    <SelectItem key={org} value={org}>
                                      <div className="flex items-center space-x-2">
                                        <Bug className="w-4 h-4 text-purple-600" />
                                        <span>{org}</span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Dialog open={showAddZooplanktonDialog} onOpenChange={setShowAddZooplanktonDialog}>
                                <DialogTrigger asChild>
                                  <Button variant="outline" className="h-12 px-4 border-2 hover:border-purple-500 hover:bg-purple-50">
                                    <Plus className="w-4 h-4" />
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>Legg til ny zooplankton-type</DialogTitle>
                                  </DialogHeader>
                                  <div className="space-y-4">
                                    <div>
                                      <Label>Prøvetype</Label>
                                      <Input
                                        value={newZooplankton.sampleType}
                                        readOnly
                                        className="h-12 bg-slate-50"
                                      />
                                    </div>
                                    <div>
                                      <Label>Organismenavn *</Label>
                                      <Input
                                        value={newZooplanktonName}
                                        onChange={(e) => setNewZooplanktonName(e.target.value)}
                                        placeholder="f.eks. Copepod"
                                        className="h-12"
                                      />
                                    </div>
                                    <div className="flex space-x-2">
                                      <Button onClick={addNewZooplanktonType} className="flex-1 bg-purple-600 hover:bg-purple-700">
                                        Legg til organisme
                                      </Button>
                                      <Button variant="outline" onClick={() => setShowAddZooplanktonDialog(false)} className="flex-1">
                                        Avbryt
                                      </Button>
                                    </div>
                                  </div>
                                </DialogContent>
                              </Dialog>
                            </div>
                          </div>

                          {/* Add button */}
                          <Button
                            onClick={addZooplankton}
                            disabled={!newZooplankton.sampleType || !newZooplankton.organism}
                            className="w-full h-12 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-xl"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Legg til zooplankton
                          </Button>
                        </div>
                      </div>

                      {/* Zooplankton list */}
                      {zooplanktonOrganisms.length === 0 ? (
                        <div className="text-center py-12 bg-slate-50 rounded-xl border-2 border-dashed border-slate-300">
                          <Bug className="w-16 h-16 mx-auto mb-3 text-slate-300" />
                          <p className="text-sm font-semibold text-slate-600 mb-1">Ingen zooplankton lagt til ennå</p>
                          <p className="text-xs text-slate-400">Velg prøvetype og organisme over for å legge til</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {zooplanktonOrganisms.map((zoo, index) => (
                            <div
                              key={zoo.id}
                              className="flex items-center justify-between p-4 bg-white rounded-xl border-2 border-slate-200 hover:border-purple-300 hover:shadow-md transition-all"
                            >
                              <div className="flex items-center space-x-4">
                                <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl flex items-center justify-center">
                                  <span className="text-sm font-bold bg-gradient-to-br from-purple-600 to-pink-600 bg-clip-text text-transparent">
                                    {index + 1}
                                  </span>
                                </div>
                                <div>
                                  <div className="font-bold text-slate-900 text-base">{zoo.organism}</div>
                                  <div className="text-xs text-slate-600">
                                    <span className="font-semibold text-purple-600">{zoo.sampleType}</span>
                                  </div>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeZooplankton(zoo.id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Right: Preview & Actions */}
            <div className="space-y-6">
              {/* Preview */}
              <Card className="shadow-xl border-0 sticky top-24">
                <div className="h-1.5 bg-gradient-to-r from-emerald-500 to-teal-500"></div>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2 text-lg">
                    <Eye className="w-5 h-5 text-emerald-600" />
                    <span>Forhåndsvisning</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3">Tabell-størrelse</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-4 bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl border-2 border-teal-200 text-center">
                        <div className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">
                          {selectedLocations.length}
                        </div>
                        <div className="text-xs text-slate-600 font-semibold mt-1">Kolonner</div>
                      </div>
                      <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200 text-center">
                        <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                          {customOrganisms.length + zooplanktonOrganisms.length}
                        </div>
                        <div className="text-xs text-slate-600 font-semibold mt-1">Rader</div>
                      </div>
                    </div>
                  </div>

                  {selectedLocationsList.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Lokaliteter</div>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedLocationsList.map((loc) => (
                          <span key={loc.id} className="px-2.5 py-1.5 bg-teal-100 text-teal-700 rounded-lg text-xs font-bold border border-teal-200">
                            {loc.letter}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {(customOrganisms.length > 0 || zooplanktonOrganisms.length > 0) && (
                    <div>
                      <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Organismer</div>
                      <div className="space-y-1 max-h-40 overflow-y-auto pr-2">
                        {customOrganisms.map((org, idx) => (
                          <div key={org.id} className="text-xs px-2.5 py-1.5 bg-blue-50 rounded-lg border border-blue-200 flex items-center justify-between">
                            <div>
                              <span className="font-bold text-blue-900">{idx + 1}.</span>
                              <span className="font-semibold text-slate-900 ml-1">{org.species}</span>
                              <span className="text-xs text-blue-600 ml-1">(alge)</span>
                            </div>
                            <span className="text-slate-500 text-xs">{org.depth}m</span>
                          </div>
                        ))}
                        {zooplanktonOrganisms.map((zoo, idx) => (
                          <div key={zoo.id} className="text-xs px-2.5 py-1.5 bg-purple-50 rounded-lg border border-purple-200 flex items-center justify-between">
                            <div>
                              <span className="font-bold text-purple-900">{customOrganisms.length + idx + 1}.</span>
                              <span className="font-semibold text-slate-900 ml-1">{zoo.organism}</span>
                              <span className="text-xs text-purple-600 ml-1">(zooplankton)</span>
                            </div>
                            <span className="text-slate-500 text-xs">{zoo.sampleType}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="pt-4 border-t border-slate-200">
                    <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl">
                      <div className="text-center">
                        <div className="text-xs text-purple-700 font-semibold uppercase tracking-wide mb-1">Totale felter</div>
                        <div className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                          {selectedLocations.length * (customOrganisms.length + zooplanktonOrganisms.length)}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Save actions */}
              <div className="space-y-3">
                <Button
                  onClick={saveTemplate}
                  disabled={!templateName || selectedLocations.length === 0 || (customOrganisms.length === 0 && zooplanktonOrganisms.length === 0)}
                  className="w-full h-14 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold text-base rounded-xl shadow-lg hover:shadow-xl transition-all"
                >
                  <Save className="w-5 h-5 mr-2" />
                  Lagre og bruk template
                </Button>
                
                <Button
                  onClick={() => setShowPreview(!showPreview)}
                  variant="outline"
                  className="w-full h-12 border-2 border-slate-200 hover:border-purple-500 hover:bg-purple-50 rounded-xl"
                  disabled={selectedLocations.length === 0 || (customOrganisms.length === 0 && zooplanktonOrganisms.length === 0)}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {showPreview ? 'Skjul' : 'Vis'} full tabell
                </Button>
              </div>
            </div>
          </div>

          {/* Table Preview Modal */}
          {showPreview && selectedLocations.length > 0 && (customOrganisms.length > 0 || zooplanktonOrganisms.length > 0) && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6">
              <Card className="max-w-7xl w-full max-h-[90vh] overflow-auto shadow-2xl">
                <CardHeader className="flex flex-row items-center justify-between border-b border-slate-200 bg-slate-50">
                  <div>
                    <CardTitle className="text-2xl">Forhåndsvisning av tabell</CardTitle>
                    <p className="text-sm text-slate-600 mt-1">
                      {selectedLocations.length} kolonner × {customOrganisms.length + zooplanktonOrganisms.length} rader = {selectedLocations.length * (customOrganisms.length + zooplanktonOrganisms.length)} felter
                    </p>
                  </div>
                  <Button variant="ghost" onClick={() => setShowPreview(false)} size="sm">
                    <X className="w-5 h-5" />
                  </Button>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead className="bg-gradient-to-r from-slate-50 to-blue-50">
                        <tr>
                          <th className="border-2 border-slate-300 px-4 py-3 text-left font-bold text-slate-700">Orden/gruppe</th>
                          <th className="border-2 border-slate-300 px-4 py-3 text-left font-bold text-slate-700">Evt. art</th>
                          <th className="border-2 border-slate-300 px-3 py-3 text-center font-bold text-slate-700">Djup (m)</th>
                          {selectedLocationsList.map(loc => (
                            <th key={loc.id} className="border-2 border-slate-300 px-3 py-3 text-center min-w-28">
                              <div className="text-sm font-bold text-slate-900">{loc.letter} - {loc.name}</div>
                              <div className="text-xs text-slate-500 font-normal">(ant/l)</div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {customOrganisms.map((organism, idx) => (
                          <tr key={organism.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="border border-slate-300 px-4 py-3 font-medium text-slate-700">{organism.group}</td>
                            <td className="border border-slate-300 px-4 py-3 italic text-slate-900">{organism.species}</td>
                            <td className="border border-slate-300 px-3 py-3 text-center font-semibold text-blue-600">{organism.depth}</td>
                            {selectedLocationsList.map(loc => (
                              <td key={loc.id} className="border border-slate-300 p-2">
                                <div className="h-10 bg-emerald-100 rounded-md flex items-center justify-center text-sm font-semibold text-slate-400">
                                  —
                                </div>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
